import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Select,
  Switch,
  useDisclosure,
  useToast,
  VStack,
  Image,
  IconButton,
  Text,
  Badge,
  Divider,
} from '@chakra-ui/react';
import { CloseIcon, ArrowBackIcon } from '@chakra-ui/icons';
import axios from 'axios';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import BlotFormatter, { ImageSpec } from 'quill-blot-formatter';
import { useHistory, useLocation } from 'react-router-dom';

// 비디오 리사이즈 지원 Spec
class VideoSpec extends ImageSpec {
  constructor(formatter) {
    super(formatter);
    this.target = null;
  }

  init() {
    this.formatter.quill.root.addEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const el = event.target;
    if (
      el instanceof HTMLElement &&
      (el.tagName === 'IMG' || el.tagName === 'VIDEO')
    ) {
      this.target = el;
      this.formatter.show(this);
    }
  };

  getTargetElement() {
    return this.target;
  }

  onHide() {
    this.target = null;
  }
}

Quill.register('modules/blotFormatter', BlotFormatter, true);

// 줄간격 모듈 등록
const Parchment = Quill.import('parchment');
const lineHeightStyle = new Parchment.Attributor.Style(
  'lineHeight',
  'line-height',
  {
    scope: Parchment.Scope.BLOCK,
    whitelist: ['1', '1.2', '1.5', '1.8', '2', '2.5', '3'],
  },
);
Quill.register(lineHeightStyle, true);

// 폰트 크기 모듈 등록 (공식 Size Attributor 사용)
const Size = Quill.import('attributors/style/size');
Size.whitelist = [
  '12px',
  '14px',
  '16px',
  '18px',
  '20px',
  '24px',
  '28px',
  '32px',
  '36px',
  '48px',
  '72px',
];
Quill.register(Size, true);

// 이미지 블롯 확장
const ImageBlot = Quill.import('formats/image');
class ResizableImageBlot extends ImageBlot {
  static create(value) {
    const node = super.create(value);
    if (typeof value === 'object') {
      node.setAttribute('src', value.src);
      if (value.width) node.setAttribute('width', value.width);
      if (value.height) node.setAttribute('height', value.height);
    }
    return node;
  }
  static value(node) {
    return {
      src: node.getAttribute('src'),
      width: node.getAttribute('width'),
      height: node.getAttribute('height'),
    };
  }
}
ResizableImageBlot.blotName = 'image';
Quill.register(ResizableImageBlot, true);

// 비디오 블롯 커스텀
const BlockEmbed = Quill.import('blots/block/embed');
class VideoBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    const src = typeof value === 'object' ? value.src : value;
    node.setAttribute('src', src);
    node.setAttribute('controls', true);
    if (typeof value === 'object') {
      if (value.width) node.setAttribute('width', value.width);
      if (value.height) node.setAttribute('height', value.height);
    }
    node.style.maxWidth = '100%';
    return node;
  }
  static value(node) {
    return {
      src: node.getAttribute('src'),
      width: node.getAttribute('width'),
      height: node.getAttribute('height'),
    };
  }
}
VideoBlot.blotName = 'video';
VideoBlot.tagName = 'video';
Quill.register(VideoBlot, true);

// 에디터 스타일
const editorStyles = `
  .blog-editor {
    width: 100%;
  }
  .blog-editor .quill {
    width: 100%;
  }
  .blog-editor .ql-container {
    font-size: 16px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .blog-editor .ql-editor {
    min-height: 600px;
    overflow-y: auto;
    padding: 24px 32px;
    line-height: 1.8;
  }
  .blog-editor .ql-editor img,
  .blog-editor .ql-editor video {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
  }
  .blog-editor .ql-toolbar {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background: #f8fafc;
    border-color: #e2e8f0;
    padding: 12px 16px;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .blog-editor .ql-container {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    border-color: #e2e8f0;
  }
  .blog-editor .ql-editor:focus {
    outline: none;
  }
  .blog-editor .ql-snow .ql-picker {
    font-size: 14px;
  }
  /* 폰트 크기 셀렉터 */
  .blog-editor .ql-snow .ql-picker.ql-size {
    width: 65px;
  }
  .blog-editor .ql-snow .ql-picker.ql-size .ql-picker-label::before,
  .blog-editor .ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: attr(data-value);
  }
  .blog-editor .ql-snow .ql-picker.ql-size .ql-picker-label:not([data-value])::before {
    content: '크기';
  }
  /* 줄간격 셀렉터 라벨 */
  .blog-editor .ql-snow .ql-picker.ql-lineHeight {
    width: 70px;
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-label::before {
    content: '줄간격';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1"]::before {
    content: '1.0';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.2"]::before {
    content: '1.2';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.5"]::before {
    content: '1.5';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="1.8"]::before {
    content: '1.8';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2"]::before {
    content: '2.0';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="2.5"]::before {
    content: '2.5';
  }
  .blog-editor .ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value="3"]::before {
    content: '3.0';
  }
  /* custom 색상 옵션 */
  .blog-editor .ql-snow .ql-color-picker .ql-picker-item[data-value="custom"],
  .blog-editor .ql-snow .ql-background .ql-picker-item[data-value="custom"] {
    background: linear-gradient(135deg, #ff0000, #00ff00, #0000ff) !important;
    position: relative;
  }
  .blog-editor .ql-snow .ql-color-picker .ql-picker-item[data-value="custom"]::after,
  .blog-editor .ql-snow .ql-background .ql-picker-item[data-value="custom"]::after {
    content: '#';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-weight: bold;
    font-size: 10px;
    text-shadow: 0 0 2px black;
  }
`;

export function BlogWrite() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const fileInputRef = useRef();
  const videoInputRef = useRef();
  const quillRef = useRef();
  const blogToUpdate = location.state?.blog;

  const HARDWARE_OPTIONS = ['빔프로젝터', 'LED', 'LED+빔프로젝터'];
  const [categories, setCategories] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailType, setThumbnailType] = useState(null);
  const [isCustomHardware, setIsCustomHardware] = useState(
    blogToUpdate?.hardware &&
      !HARDWARE_OPTIONS.includes(blogToUpdate?.hardware),
  );
  const [content, setContent] = useState(blogToUpdate?.content || '');
  const [form, setForm] = useState({
    title: blogToUpdate?.title || '',
    categoryId: blogToUpdate?.category?.id || '',
    thumbnailImageUrl: blogToUpdate?.thumbnailImageUrl || '',
    thumbnailVideoUrl: blogToUpdate?.thumbnailVideoUrl || '',
    industry: blogToUpdate?.industry || '',
    hardware: blogToUpdate?.hardware || '',
    isPublished: blogToUpdate?.isPublished || false,
  });

  useEffect(() => {
    axios.get('/admin/blog-categories').then((res) => setCategories(res.data));
  }, []);

  useEffect(() => {
    if (blogToUpdate?.thumbnailVideoUrl) {
      setThumbnailPreview(blogToUpdate.thumbnailVideoUrl);
      setThumbnailType('video');
    } else if (blogToUpdate?.thumbnailImageUrl) {
      setThumbnailPreview(blogToUpdate.thumbnailImageUrl);
      setThumbnailType('image');
    }
  }, [blogToUpdate]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      toast({
        title: '이미지 또는 비디오 파일만 업로드 가능합니다.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setThumbnailPreview(previewUrl);
    setThumbnailType(isVideo ? 'video' : 'image');

    const formData = new FormData();
    formData.append('files', file);
    try {
      const res = await axios.post('/utils/upload-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedUrl = res.data[0].url;

      if (isVideo) {
        setForm({
          ...form,
          thumbnailVideoUrl: uploadedUrl,
          thumbnailImageUrl: '',
        });
      } else {
        setForm({
          ...form,
          thumbnailImageUrl: uploadedUrl,
          thumbnailVideoUrl: '',
        });
      }
      setThumbnailPreview(uploadedUrl);
    } catch (error) {
      toast({ title: '업로드 실패', status: 'error', isClosable: true });
      setThumbnailPreview(null);
      setThumbnailType(null);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailPreview(null);
    setThumbnailType(null);
    setForm({ ...form, thumbnailImageUrl: '', thumbnailVideoUrl: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('files', file);
      try {
        const res = await axios.post('/utils/upload-files', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = res.data[0].url;
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', url);
        quill.setSelection(range.index + 1);
      } catch (error) {
        toast({
          title: '이미지 업로드 실패',
          status: 'error',
          isClosable: true,
        });
      }
    };
  };

  const videoHandler = () => {
    videoInputRef.current?.click();
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (videoInputRef.current) videoInputRef.current.value = '';

    const formData = new FormData();
    formData.append('files', file);
    try {
      const res = await axios.post('/utils/upload-files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data[0].url;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'video', url);
      quill.setSelection(range.index + 1);
      toast({
        title: '비디오가 삽입되었습니다.',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      toast({ title: '비디오 업로드 실패', status: 'error', isClosable: true });
    }
  };

  // 최근 사용 색상 관리
  const RECENT_COLORS_KEY = 'blog-editor-recent-colors';
  const MAX_RECENT_COLORS = 8;

  const getRecentColors = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_COLORS_KEY)) || [];
    } catch {
      return [];
    }
  };

  const saveRecentColor = (color) => {
    const recent = getRecentColors().filter((c) => c !== color);
    recent.unshift(color);
    localStorage.setItem(
      RECENT_COLORS_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_COLORS)),
    );
  };

  // 커스텀 색상 핸들러
  const colorInputRef = useRef();
  const colorTypeRef = useRef('color');
  const colorRangeRef = useRef(null);

  const colorHandler = (type) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    colorTypeRef.current = type;
    colorRangeRef.current = quill.getSelection(true);
    const currentColor = quill.getFormat()[type] || '#000000';
    colorInputRef.current.value = currentColor;
    colorInputRef.current.click();
  };

  const handleColorChange = (e) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const color = e.target.value;
    const range = colorRangeRef.current;

    setTimeout(() => {
      quill.focus();
      if (range) {
        quill.setSelection(range.index, range.length);
      }
      quill.format(colorTypeRef.current, color);
      saveRecentColor(color);
      setRecentColors(getRecentColors());
    }, 0);
  };

  // 기본 색상 팔레트
  const baseColors = [
    '#000000',
    '#e60000',
    '#ff9900',
    '#ffff00',
    '#008a00',
    '#0066cc',
    '#9933ff',
    '#ffffff',
    '#facccc',
    '#ffebcc',
    '#ffffcc',
    '#cce8cc',
    '#cce0f5',
    '#ebd6ff',
    '#bbbbbb',
    '#f06666',
    '#ffc266',
    '#ffff66',
    '#66b966',
    '#66a3e0',
    '#c285ff',
    '#888888',
    '#a10000',
    '#b26b00',
    '#b2b200',
    '#006100',
    '#0047b2',
    '#6b24b2',
    '#444444',
    '#5c0000',
    '#663d00',
    '#666600',
    '#003700',
    '#002966',
    '#3d1466',
  ];

  const [recentColors, setRecentColors] = useState(getRecentColors());
  const colorOptions = [...new Set([...recentColors, ...baseColors]), 'custom'];

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          [
            {
              size: [
                '12px',
                '14px',
                '16px',
                '18px',
                '20px',
                '24px',
                '28px',
                '32px',
                '36px',
                '48px',
                '72px',
              ],
            },
          ],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: colorOptions }, { background: colorOptions }],
          [{ lineHeight: ['1', '1.2', '1.5', '1.8', '2', '2.5', '3'] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image', 'video'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
          video: videoHandler,
          color: function (value) {
            if (value === 'custom') {
              colorHandler('color');
            } else {
              this.quill.format('color', value);
            }
          },
          background: function (value) {
            if (value === 'custom') {
              colorHandler('background');
            } else {
              this.quill.format('background', value);
            }
          },
        },
      },
      blotFormatter: {
        specs: [VideoSpec],
      },
    }),
    [recentColors],
  );

  const formats = [
    'header',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'lineHeight',
    'list',
    'bullet',
    'align',
    'link',
    'image',
    'video',
  ];

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast({
        title: '제목을 입력해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    if (!form.categoryId) {
      toast({
        title: '카테고리를 선택해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    if (!form.thumbnailImageUrl && !form.thumbnailVideoUrl) {
      toast({
        title: '썸네일을 업로드해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    if (!content.trim() || content === '<p><br></p>') {
      toast({
        title: '내용을 입력해주세요.',
        status: 'warning',
        isClosable: true,
      });
      return;
    }
    try {
      const payload = {
        ...form,
        content,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
      };
      if (blogToUpdate) {
        await axios.patch(`/admin/blogs/${blogToUpdate.id}`, payload);
      } else {
        await axios.post('/admin/blogs', payload);
      }
      toast({
        title: `블로그가 ${blogToUpdate ? '수정' : '등록'}되었습니다.`,
        status: 'success',
        isClosable: true,
      });
      history.push('/admin/blogs');
    } catch (error) {
      toast({
        title: error.response?.data?.message || '오류가 발생했습니다.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const onDelete = async () => {
    if (!blogToUpdate) return;
    try {
      await axios.delete(`/admin/blogs/${blogToUpdate.id}`);
      toast({
        title: '블로그가 삭제되었습니다.',
        status: 'success',
        isClosable: true,
      });
      onClose();
      history.push('/admin/blogs');
    } catch (error) {
      toast({
        title: error.response?.data?.message || '오류가 발생했습니다.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  return (
    <>
      <style>{editorStyles}</style>
      <input
        type="file"
        accept="video/mp4,video/webm,video/ogg"
        ref={videoInputRef}
        onChange={handleVideoUpload}
        style={{ display: 'none' }}
      />
      <input
        type="color"
        ref={colorInputRef}
        onChange={handleColorChange}
        style={{ position: 'absolute', visibility: 'hidden' }}
      />

      <Box pt={{ base: '120px', md: '75px' }} maxW="1600px" mx="auto" px={6}>
        {/* 헤더 */}
        <Flex justify="space-between" align="center" mb={6}>
          <HStack spacing={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              variant="ghost"
              onClick={() => history.push('/admin/blogs')}
              aria-label="뒤로가기"
              size="lg"
            />
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                {blogToUpdate ? '블로그 수정' : '새 블로그 작성'}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {blogToUpdate
                  ? `ID: ${blogToUpdate.id}`
                  : '새로운 블로그 포스트를 작성합니다'}
              </Text>
            </Box>
          </HStack>

          <HStack spacing={3}>
            <HStack spacing={2} bg="gray.50" px={4} py={2} borderRadius="lg">
              <Text fontSize="sm" color="gray.600">
                발행 상태
              </Text>
              <Switch
                id="isPublished"
                name="isPublished"
                isChecked={form.isPublished}
                onChange={handleChange}
                colorScheme="green"
                size="lg"
              />
              <Badge
                colorScheme={form.isPublished ? 'green' : 'gray'}
                fontSize="sm"
              >
                {form.isPublished ? '발행됨' : '비공개'}
              </Badge>
            </HStack>
            {blogToUpdate && (
              <Button
                colorScheme="red"
                variant="outline"
                onClick={onOpen}
                size="md"
              >
                삭제
              </Button>
            )}
            <Button colorScheme="blue" onClick={handleSave} size="md" px={8}>
              {blogToUpdate ? '수정 완료' : '저장'}
            </Button>
          </HStack>
        </Flex>

        {/* 제목 입력 */}
        <Card mb={6}>
          <CardBody py={4}>
            <Input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="제목을 입력하세요"
              size="lg"
              fontSize="2xl"
              fontWeight="bold"
              variant="unstyled"
              _placeholder={{ color: 'gray.400' }}
            />
          </CardBody>
        </Card>

        <Grid templateColumns={{ base: '1fr', xl: '1fr 360px' }} gap={6}>
          {/* 메인 에디터 영역 */}
          <GridItem>
            <Card>
              <CardBody p={0}>
                {/* 에디터 */}
                <Box className="blog-editor" w="100%">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                  />
                </Box>
              </CardBody>
            </Card>
          </GridItem>

          {/* 사이드바 - 메타 정보 */}
          <GridItem>
            <VStack spacing={5} position="sticky" top="90px">
              {/* 썸네일 */}
              <Card w="100%">
                <CardHeader pb={2}>
                  <Text fontWeight="semibold" color="gray.700">
                    썸네일{' '}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </Text>
                </CardHeader>
                <CardBody pt={2}>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/webm,video/ogg"
                    ref={fileInputRef}
                    onChange={handleThumbnailUpload}
                    style={{ display: 'none' }}
                  />
                  {thumbnailPreview ? (
                    <Box
                      position="relative"
                      borderRadius="lg"
                      overflow="hidden"
                      bg="gray.100"
                    >
                      {thumbnailType === 'video' ? (
                        <video
                          src={thumbnailPreview}
                          controls
                          style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Image
                          src={thumbnailPreview}
                          w="100%"
                          h="200px"
                          objectFit="cover"
                        />
                      )}
                      <IconButton
                        icon={<CloseIcon />}
                        size="sm"
                        colorScheme="blackAlpha"
                        position="absolute"
                        top={2}
                        right={2}
                        onClick={handleRemoveThumbnail}
                        aria-label="썸네일 삭제"
                        borderRadius="full"
                      />
                      <Badge
                        position="absolute"
                        bottom={2}
                        left={2}
                        colorScheme={
                          thumbnailType === 'video' ? 'purple' : 'blue'
                        }
                      >
                        {thumbnailType === 'video' ? '비디오' : '이미지'}
                      </Badge>
                    </Box>
                  ) : (
                    <Box
                      border="2px dashed"
                      borderColor="gray.200"
                      borderRadius="lg"
                      p={8}
                      textAlign="center"
                      cursor="pointer"
                      onClick={() => fileInputRef.current.click()}
                      _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
                      transition="all 0.2s"
                    >
                      <Text color="gray.500" fontSize="sm">
                        클릭하여 이미지 또는 비디오 업로드
                      </Text>
                      <Text color="gray.400" fontSize="xs" mt={1}></Text>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* 분류 정보 */}
              <Card w="100%">
                <CardHeader pb={2}>
                  <Text fontWeight="semibold" color="gray.700">
                    분류
                  </Text>
                </CardHeader>
                <CardBody pt={2}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.600">
                        카테고리
                      </FormLabel>
                      <Select
                        name="categoryId"
                        value={form.categoryId}
                        onChange={handleChange}
                        placeholder="선택"
                        size="md"
                      >
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">
                        업종
                      </FormLabel>
                      <Input
                        name="industry"
                        value={form.industry}
                        onChange={handleChange}
                        placeholder="예: 카페, 레스토랑"
                        size="md"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.600">
                        하드웨어
                      </FormLabel>
                      <Select
                        value={
                          HARDWARE_OPTIONS.includes(form.hardware)
                            ? form.hardware
                            : isCustomHardware
                            ? 'custom'
                            : ''
                        }
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setIsCustomHardware(true);
                            setForm({ ...form, hardware: '' });
                          } else {
                            setIsCustomHardware(false);
                            setForm({ ...form, hardware: e.target.value });
                          }
                        }}
                        placeholder="선택"
                        size="md"
                      >
                        <option value="빔프로젝터">빔프로젝터</option>
                        <option value="LED">LED</option>
                        <option value="LED+빔프로젝터">LED+빔프로젝터</option>
                        <option value="custom">직접입력</option>
                      </Select>
                      {isCustomHardware && (
                        <Input
                          mt={2}
                          placeholder="하드웨어 직접 입력"
                          value={form.hardware}
                          onChange={(e) =>
                            setForm({ ...form, hardware: e.target.value })
                          }
                          size="md"
                        />
                      )}
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>
        </Grid>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              블로그 삭제
            </AlertDialogHeader>
            <AlertDialogBody>
              이 작업은 되돌릴 수 없습니다. 정말로 삭제하시겠습니까?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                취소
              </Button>
              <Button colorScheme="red" onClick={onDelete} ml={3}>
                삭제
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
